<template>
	<div class="accessible-manager">
		<slot />
	</div>
</template>

<script>
import { createLogger } from '@/helpers/log'
import router from '@/router'

const log = createLogger('AccessibleManager')

export const ACTIVE_ELEM_CLASS = 'global-active-element'
export const ACTIVE_ELEM_SELECTOR = `.${ACTIVE_ELEM_CLASS}`

export default {
	name: 'AccessibleManager',
	props: {
		sectionOrder: Array,
	},

	provide() {
		return {
			moveToNextSection: this.moveToNextSection,
			moveToPrevSection: this.moveToPrevSection,
			moveToSectionByName: this.moveToSectionByName,
		}
	},

	mounted() {
		this.focusedElement = null
		document.addEventListener('focusin', this.onFocusIn)

		this.routerHook = router.afterEach((to, from) => {
			if (to.name !== from.name) {
				// if navigating to a new route, reset to the start of the section list
				this.moveToSectionAtIndex(0)
			}
		})
	},

	beforeUnmount() {
		this.routerHook?.()
		document.removeEventListener('focusin', this.onFocusIn)
	},

	methods: {
		moveToNextSection(fromSection, currCount = 0) {
			if (currCount > 10) return
			const currentIndex = this.sectionOrder.indexOf(fromSection)
			const worked = this.moveToSectionAtIndex(currentIndex + 1)
			if (!worked) {
				this.moveToNextSection(
					this.sectionOrder[currentIndex + 1],
					currCount + 1
				)
			}
		},

		moveToPrevSection(fromSection, currCount = 0) {
			if (currCount > 10) return
			const currentIndex = this.sectionOrder.indexOf(fromSection)
			const worked = this.moveToSectionAtIndex(currentIndex - 1)
			if (!worked) {
				this.moveToPrevSection(
					this.sectionOrder[currentIndex - 1],
					currCount + 1
				)
			}
		},

		moveToSectionByName(section) {
			const currentIndex = this.sectionOrder.indexOf(section)
			this.moveToSectionAtIndex(currentIndex)
		},

		moveToSectionAtIndex(index) {
			if (index >= this.sectionOrder.length) {
				index = 0
			}
			index < 0 && (index = this.sectionOrder.length - 1)
			document.activeElement?.blur?.()
			return this.focusOnSection(this.sectionOrder[index])
		},

		focusOnSection(sectionName) {
			if (!sectionName) return false
			let elem = document.querySelector(`[data-ally-section="${sectionName}"]`)
			log('attempting to focus section', sectionName, elem)
			if (!elem?.__vnode) return false
			if (elem.__vnode.focusMe) {
				elem.__vnode.focusMe()
			} else {
				let child = elem.__vnode?.ctx
				while (
					child &&
					!child.focusMe &&
					(child.children?.length || child.ctx)
				) {
					child = child.ctx?.focusMe ? child.ctx : child.children?.[0]?.ctx
				}
				if (child?.focusMe) {
					child?.focusMe?.()
				} else {
					log('failed to find focusable child', elem.__vnode)
				}
			}
			return true
		},

		onFocusIn() {
			const { activeElement } = document
			if (this.focusedElement && this.focusedElement !== activeElement) {
				this.focusedElement.classList.remove(ACTIVE_ELEM_CLASS)
			}
			this.focusedElement = activeElement
			this.focusedElement?.classList.add(ACTIVE_ELEM_CLASS)
		},
	},
}
</script>

<style lang="scss" scoped></style>
